import React, { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";
import {
  getCertificateFileUrl,
  isPdfFile,
} from "@/utils/certificateFile";

const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];

const CertificateFileUploader = ({ onChange, value = null }) => {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [isPdf, setIsPdf] = useState(false);

  useEffect(() => {
    if (!value) {
      setPreview(null);
      setIsPdf(false);
      return;
    }

    if (value instanceof File || value instanceof Blob) {
      const pdf = value.type === "application/pdf";
      setIsPdf(pdf);
      if (pdf) {
        setPreview(value.name);
      } else {
        setPreview(URL.createObjectURL(value));
      }
      return;
    }

    const pdf = isPdfFile(value);
    setIsPdf(pdf);
    setPreview(getCertificateFileUrl(value) || (typeof value === "string" ? value : value?.url));
  }, [value]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      alert("Only PNG, JPG, and PDF files are allowed.");
      e.target.value = "";
      return;
    }

    onChange(file);
    e.target.value = "";
  };

  const handleRemove = () => {
    onChange(null);
    setPreview(null);
    setIsPdf(false);
  };

  return (
    <div className="space-y-3">
      {preview ? (
        <div className="relative inline-block">
          {isPdf ? (
            <div className="w-40 h-40 flex flex-col items-center justify-center rounded-lg border bg-muted gap-2 p-3">
              <FileText className="w-10 h-10 text-red-500" />
              <span className="text-xs text-muted-foreground text-center line-clamp-2 break-all">
                {typeof preview === "string" && !preview.startsWith("http") && !preview.startsWith("blob")
                  ? preview
                  : "PDF Certificate"}
              </span>
            </div>
          ) : (
            <img
              src={preview}
              alt="Certificate preview"
              className="w-40 h-40 object-cover rounded-lg border"
            />
          )}
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full shadow hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-40 h-40 flex flex-col items-center justify-center rounded-lg border border-dashed bg-muted cursor-pointer hover:bg-accent transition gap-2"
        >
          <ImageIcon className="w-8 h-8 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">PNG, JPG, PDF</span>
        </div>
      )}

      <input
        type="file"
        accept="image/png,image/jpeg,image/jpg,application/pdf"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileSelect}
      />

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-4 h-4 mr-2" />
        {preview ? "Replace File" : "Upload File"}
      </Button>
    </div>
  );
};

export default CertificateFileUploader;
